/**
 * Shopify Change Service
 * 
 * Handles tracking and applying changes to products, including option updates,
 * variant modifications, and SKU changes according to Shopify Admin API 2025-01
 * specifications.
 */

export class ShopifyChangeService {
  constructor(graphqlService) {
    this.graphqlService = graphqlService;
  }

  /**
   * Processes changes for product options
   * @param {Array} currentOptions - Current product options
   * @param {object} changes - Change data
   * @returns {Array} Updated options
   */
  processOptionChanges(currentOptions, changes) {
    if (!changes?.options || !Array.isArray(currentOptions)) {
      return currentOptions;
    }

    return currentOptions.map((option, index) => {
      const updatedOption = { ...option };
      
      if (changes.options[index]?.values) {
        updatedOption.values = changes.options[index].values;
      }

      return updatedOption;
    });
  }

  /**
   * Processes changes for variants
   * @param {Array} currentVariants - Current variants
   * @param {object} changes - Change data
   * @returns {object} Updated variants and change maps
   */
  processVariantChanges(currentVariants, changes) {
    const skuChangeMap = {};
    const updatedVariants = [];

    if (!Array.isArray(currentVariants)) {
      return { variants: [], skuChangeMap };
    }

    currentVariants.forEach(variant => {
      const updatedVariant = { ...variant };
      const originalSku = variant.sku;

      // Apply SKU changes if present
      if (changes?.skus && changes.skus[originalSku]) {
        updatedVariant.sku = changes.skus[originalSku];
        skuChangeMap[originalSku] = updatedVariant.sku;
      }

      // Apply price changes if present
      if (changes?.prices && changes.prices[originalSku]) {
        updatedVariant.price = changes.prices[originalSku];
      }

      // Apply inventory changes if present
      if (changes?.inventory && changes.inventory[originalSku] !== undefined) {
        updatedVariant.inventoryQuantity = changes.inventory[originalSku];
      }

      updatedVariants.push(updatedVariant);
    });

    return {
      variants: updatedVariants,
      skuChangeMap
    };
  }

  /**
   * Processes changes for variant options
   * @param {Array} variants - Variants to update
   * @param {Array} productOptions - Product options
   * @param {object} changes - Change data
   * @returns {Array} Updated variants with new option values
   */
  processVariantOptionChanges(variants, productOptions, changes) {
    if (!Array.isArray(variants) || !Array.isArray(productOptions)) {
      return variants;
    }

    return variants.map(variant => {
      const updatedVariant = { ...variant };
      
      if (variant.options && changes?.options) {
        updatedVariant.options = variant.options.map((optionValue, index) => {
          // Check if we have an update for this option
          if (changes.options[index]?.values) {
            const oldValueIndex = productOptions[index].values.indexOf(optionValue);
            if (oldValueIndex !== -1 && oldValueIndex < changes.options[index].values.length) {
              return changes.options[index].values[oldValueIndex];
            }
          }
          return optionValue;
        });
      }

      return updatedVariant;
    });
  }

  /**
   * Processes changes for variant images
   * @param {Array} variants - Variants to update
   * @param {object} changes - Change data
   * @returns {object} Updated image associations
   */
  processVariantImageChanges(variants, changes) {
    const imageAssociations = {};

    if (!Array.isArray(variants) || !changes?.variantImages) {
      return imageAssociations;
    }

    variants.forEach(variant => {
      if (changes.variantImages[variant.sku]) {
        imageAssociations[variant.sku] = changes.variantImages[variant.sku];
      }
    });

    return imageAssociations;
  }

  /**
   * Tracks changes between original and updated variants
   * @param {Array} originalVariants - Original variants
   * @param {Array} updatedVariants - Updated variants
   * @returns {object} Change tracking data
   */
  trackVariantChanges(originalVariants, updatedVariants) {
    const changes = {
      skus: {},
      prices: {},
      inventory: {},
      options: {}
    };

    if (!Array.isArray(originalVariants) || !Array.isArray(updatedVariants)) {
      return changes;
    }

    originalVariants.forEach((original, index) => {
      const updated = updatedVariants[index];
      if (!updated) return;

      // Track SKU changes
      if (original.sku !== updated.sku) {
        changes.skus[original.sku] = updated.sku;
      }

      // Track price changes
      if (original.price !== updated.price) {
        changes.prices[original.sku] = updated.price;
      }

      // Track inventory changes
      if (original.inventoryQuantity !== updated.inventoryQuantity) {
        changes.inventory[original.sku] = updated.inventoryQuantity;
      }

      // Track option changes
      if (original.options && updated.options) {
        original.options.forEach((value, optionIndex) => {
          if (value !== updated.options[optionIndex]) {
            if (!changes.options[optionIndex]) {
              changes.options[optionIndex] = { values: [] };
            }
            changes.options[optionIndex].values[index] = updated.options[optionIndex];
          }
        });
      }
    });

    return changes;
  }

  /**
   * Validates that changes are safe to apply
   * @param {object} changes - Changes to validate
   * @returns {object} Validation result
   */
  validateChanges(changes) {
    const errors = [];

    // Validate SKU changes
    if (changes.skus) {
      const newSkus = new Set(Object.values(changes.skus));
      if (newSkus.size !== Object.keys(changes.skus).length) {
        errors.push('Duplicate SKUs in changes');
      }
    }

    // Validate price changes
    if (changes.prices) {
      Object.entries(changes.prices).forEach(([sku, price]) => {
        if (isNaN(parseFloat(price)) || parseFloat(price) < 0) {
          errors.push(`Invalid price for SKU ${sku}`);
        }
      });
    }

    // Validate inventory changes
    if (changes.inventory) {
      Object.entries(changes.inventory).forEach(([sku, quantity]) => {
        if (!Number.isInteger(quantity) || quantity < 0) {
          errors.push(`Invalid inventory quantity for SKU ${sku}`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
